/**
 * @feature landing-page
 * @owner das-finsuite-application-dev
 * @description Computes fiscal quarter progress (% complete, days remaining).
 *
 * Period = calendar quarter start → end of extended close month.
 * E.g. Q2: Apr 1 → Jul 31. Pure date math, no API call.
 */

import { QUARTER_START_MONTHS, QUARTER_END_MONTHS } from 'src/components/dfp-home-page/landing-page/fiscal-quarter-constants';

export interface QuarterProgress {
  quarter: string; // e.g. "Q2 FY26"
  percentComplete: number; // 0–100
  daysRemaining: number;
}

function getLastDayOfMonth(year: number, month: number): Date {
  const d = new Date(year, month, 0);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * Returns the current quarter progress.
 * Deterministic — no async, no side effects.
 */
export function getQuarterProgress(now: Date = new Date()): QuarterProgress {
  const currentMonth = now.getMonth() + 1; // 1-indexed
  const currentYear = now.getFullYear();

  // Find which quarter we're in by checking which period [start..closeEnd] contains today
  let quarterIndex = -1;
  let periodStart: Date | null = null;
  let periodEnd: Date | null = null;

  for (let i = 0; i < QUARTER_START_MONTHS.length; i++) {
    const startMonth = QUARTER_START_MONTHS[i];
    const endMonth = QUARTER_END_MONTHS[i];

    let startYear = currentYear;
    let endYear = currentYear;

    // Handle Q4 where close month (Jan) wraps to next year
    if (endMonth < startMonth) {
      if (currentMonth >= startMonth) {
        endYear = currentYear + 1;
      } else if (currentMonth <= endMonth) {
        startYear = currentYear - 1;
      } else {
        continue;
      }
    }

    const start = new Date(startYear, startMonth - 1, 1);
    const end = getLastDayOfMonth(endYear, endMonth);

    if (now >= start && now <= end) {
      quarterIndex = i;
      periodStart = start;
      periodEnd = end;
      break;
    }
  }

  // Fallback (shouldn't happen with correct constants)
  if (quarterIndex === -1 || !periodStart || !periodEnd) {
    return { quarter: `Q? ${currentYear}`, percentComplete: 0, daysRemaining: 0 };
  }

  const fiscalYear = periodStart.getFullYear();
  const quarter = `Q${quarterIndex + 1} ${fiscalYear}`;

  const totalMs = periodEnd.getTime() - periodStart.getTime();
  const elapsedMs = now.getTime() - periodStart.getTime();
  const percentComplete = Math.min(100, Math.round((elapsedMs / totalMs) * 100));

  const msPerDay = 86400000;
  const daysRemaining = Math.max(0, Math.ceil((periodEnd.getTime() - now.getTime()) / msPerDay));

  return { quarter, percentComplete, daysRemaining };
}
